import React, { FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { ComponentThemeProvider } from '@empiriecom/module-components/ThemeProvider';
import { AvailabilityState } from '@/interfaces';
import { BoxIcon, TruckIcon } from '../../icons';
import { AvailabilityMessageLayout, availabilityMessageTheme } from './theme';

interface AvailabilityMessageProps {
  /** The availability state. */
  state: AvailabilityState;
  /** The availability message. */
  message: string;
  /** Flag for carrier delivery indication.
   * @default false
   */
  isCarrierDelivery?: boolean;
  /** The availability message layout.
   * @default 'default'
   */
  layout?: AvailabilityMessageLayout;
}

const StyledAvailabilityMessage = styled.div<{ state: string; layout: AvailabilityMessageLayout }>`
  ${({ theme: { component }, state, layout }) => component.availability[layout][state].font};
  font-size: ${({ theme: { component }, state, layout }) =>
    component.availability[layout][state].fontSize};
  line-height: ${({ theme: { component }, state, layout }) =>
    component.availability[layout][state].lineHeight};
  color: ${({ theme: { component }, state, layout }) =>
    component.availability[layout][state].color};
  margin: 0.25rem 0.75rem;
  display: flex;
  justify-content: ${({ theme: { component }, state, layout }) =>
    component.availability[layout][state].justifyContent};
  align-items: center;
`;

const Box = styled(BoxIcon)`
  color: ${({ theme: { base } }) => base.color.text};
  margin-right: 0.5rem;
`;

const Truck = styled(TruckIcon)`
  color: ${({ theme: { base } }) => base.color.text};
  margin-right: 0.5rem;
`;

/**
 * <p>This is the availability message.</p>
 * <p>It is sensible to the following <strong>three</strong> availability states:</p>
 * <ul>
 *  <li>`AVAILABLE` (green)</li>
 *  <li>`NOT_YET_AVAILABLE` (orange)</li>
 *  <li>`NOT_AVAILABLE` (red)</li>
 * </ul>
 * <p>The layout depends on the given state and the given message.</p>
 */
export const AvailabilityMessage: FunctionComponent<AvailabilityMessageProps> = ({
  state,
  message,
  isCarrierDelivery = false,
  layout = 'default',
}): JSX.Element => (
  // const isMobile = useMedia({ mobile: true, tablet: false, desktop: false });

  <ComponentThemeProvider theme={availabilityMessageTheme}>
    <StyledAvailabilityMessage
      data-testid={`availability-message-${state}`}
      state={state}
      layout={layout}
    >
      {state === 'UNSELECTED' ? (
        <>
          <Box size="1.5rem" />
          <FormattedMessage
            id="availabilityMessage.unselected"
            defaultMessage="Wähle bitte eine Variante um Lieferinformationen zu sehen"
          />
        </>
      ) : (
        <>
          {isCarrierDelivery ? <Truck size="1.5rem" /> : <Box size="1.5rem" />}
          {message || (
            <FormattedMessage
              id="availabilityMessage.fallback"
              defaultMessage="Eine Lieferaussage ist zur Zeit leider nicht möglich"
            />
          )}
        </>
      )}
    </StyledAvailabilityMessage>
  </ComponentThemeProvider>
);

export default AvailabilityMessage;
